/**
 * InlineTextArea Component (T105)
 * Editable multi-line textarea for inline editing
 * 
 * Features:
 * - Multi-line textarea with auto-focus
 * - Save/cancel buttons
 * - Ctrl/Cmd+Enter to save (Enter creates line break)
 * - Escape key to cancel
 * - Auto-resize to fit content
 * - Loading state during save
 * 
 * @example
 * <InlineTextArea
 *   value={currentValue}
 *   onChange={handleChange}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 * />
 */

import { useEffect, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { Textarea } from '@/shared/components/ui/textarea';
import { cn } from '@/lib/utils';

type InlineTextAreaProps = {
  /** Current textarea value */
  value: string;
  
  /** Callback when value changes */
  onChange: (value: string) => void;
  
  /** Callback when save is triggered */
  onSave: () => void | Promise<void>;
  
  /** Callback when cancel is triggered */
  onCancel: () => void;
  
  /** Optional placeholder text */
  placeholder?: string;
  
  /** Optional CSS class */
  className?: string;
  
  /** Minimum number of rows */
  rows?: number;
};

/**
 * InlineTextArea Component
 * 
 * Multi-line textarea with inline save/cancel actions
 * Supports Ctrl/Cmd+Enter to save, Escape to cancel
 * Enter key creates line breaks (not save)
 * Auto-focuses on mount for immediate editing
 */
export function InlineTextArea({
  value,
  onChange,
  onSave,
  onCancel,
  placeholder,
  className,
  rows = 4,
}: InlineTextAreaProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Auto-focus on mount
  useEffect(() => {
    textareaRef.current?.focus();
    // Select all text for easy replacement
    textareaRef.current?.select();
  }, []);

  // Handle keyboard shortcuts
  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl/Cmd+Enter to save
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      await handleSave();
    }
    // Escape to cancel
    else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
    // Enter alone creates line break (don't prevent default)
  };

  // Handle save with loading state
  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave();
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* Textarea */}
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={isSaving}
        rows={rows}
        className="resize-y"
      />

      {/* Action Buttons */}
      <div className="flex items-center gap-2">
        {/* Save Button */}
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Save changes"
        >
          {isSaving ? (
            <>
              <span className="text-xs">...</span>
              <span>Saving</span>
            </>
          ) : (
            <>
              <Check className="h-4 w-4" />
              <span>Save</span>
            </>
          )}
        </button>

        {/* Cancel Button */}
        <button
          type="button"
          onClick={onCancel}
          disabled={isSaving}
          className="inline-flex items-center gap-2 rounded bg-gray-600 px-3 py-1.5 text-sm text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Cancel editing"
        >
          <X className="h-4 w-4" />
          <span>Cancel</span>
        </button>

        {/* Hint text */}
        <span className="text-xs text-gray-500">
          {isSaving ? 'Saving...' : 'Ctrl+Enter to save, Esc to cancel'}
        </span>
      </div>
    </div>
  );
}

